import { CartItem, useCart as useNewCart, useSideCart } from '@grupoboticario/barril-del-chavo'
import { ecommerceEvent, ProductItem, slugify } from '@grupoboticario/tag-book-sellout'
import { useCallback } from 'react'
import { IProduct } from 'src/@interfaces'
import { getConfig } from 'src/helpers'

class AddToCartError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AddToCartError'
  }
}

export type AddToCartReason = 'drawer' | 'pin'

export const useCart = () => {
  const {
    addItem,
    removeItem,
    totalItems,
    cart,
    isLoading,
    cartMenu: { notify: notifyBag, openBag },
  } = useNewCart()
  const { businessUnit } = getConfig()
  const hasSideCartEnabled = useSideCart()

  const addToCart = useCallback(
    async (products: IProduct[], productListName?: string) => {
      try {
        for (const product of products) {
          await addItem(product, businessUnit)
        }

        if (hasSideCartEnabled) {
          openBag()
        } else {
          notifyBag()
        }

        ecommerceEvent({
          eventName: 'add_to_cart',
          products: products.map(
            (p) => ({ ...p, subCategory: p.subcategory, quantity: 1 }) as unknown as ProductItem,
          ),
          parameters: {
            item_list_id: productListName ? slugify(productListName) : 'catalogo',
            item_list_name: productListName ?? 'Catálogo',
          },
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Erro adding product to cart', error)
        window.NREUM.noticeError(new AddToCartError(`Failed to add products to cart: ${error}`), {
          origin: 'MLD/SideCart',
          brand: process.env.REACT_APP_CURRENT_BRAND,
          params: {
            products,
          },
        })
      }
    },
    [hasSideCartEnabled, addItem, businessUnit, openBag, notifyBag],
  )

  const removeFromCart = useCallback(
    (product: IProduct) => {
      removeItem({
        id: product.sku,
      } as CartItem)
    },
    [removeItem],
  )

  return {
    isLoading,
    addToCart,
    removeFromCart,
    totalCartItems: totalItems,
    currentCart: cart,
  }
}
